// src/components/entities/InvoicingTab.tsx — a company's invoicing details (finance Phase 5): what its printable
// invoices show. Logo (PNG / JPEG / WebP, up to 1 MB, embedded in every document), phone, e-mail, website, payment
// terms, the bank account to print, a footer note and the document title. GET / PUT /entities/:id/invoicing; the logo
// through POST / DELETE /entities/:id/logo. Editable with entities.update or invoices.update, read-only otherwise.

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageOff, Upload as UploadIcon } from 'lucide-react';
import { api } from '../../lib/api';
import { useCan } from '../../hooks/usePermission';
import { useStoredImage } from '../../lib/parking';
import { fileToUpload } from '../../lib/reservations';
import { Button, Card, Field, Input, KeyValue, Select, Textarea, Skeleton, SkeletonRows } from '../ui';
import { FormError, FormGrid, Wide, nulled } from '../forms/common';
import type { BankAccountSummary, InvoiceDocumentTitle, InvoicingSettings, InvoicingSettingsBody } from '../../types/api';
import { PhoneInput } from '../ui/PhoneInput';
import { formatPhone } from '../../lib/phone';

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_LOGO_BYTES = 1024 * 1024;
const TITLE_LABEL: Record<InvoiceDocumentTitle, string> = { VAT_INVOICE: 'VAT Invoice', INVOICE: 'Invoice' };

type Form = { phone: string; email: string; website: string; paymentTermsDays: string; paymentTermsLabel: string; bankAccountId: string; footerNote: string; documentTitle: '' | InvoiceDocumentTitle };

const toForm = (s: InvoicingSettings): Form => ({
  phone: s.phone ?? '', email: s.email ?? '', website: s.website ?? '', paymentTermsDays: String(s.paymentTermsDays), paymentTermsLabel: s.paymentTermsLabel ?? '',
  bankAccountId: s.bankAccountId ?? '', footerNote: s.footerNote ?? '', documentTitle: s.documentTitleIsDefault ? '' : s.documentTitle,
});

const bankLine = (b: BankAccountSummary) => [b.name, b.bankName, b.sortCode, b.accountNumber ?? b.iban].filter(Boolean).join(' · ');

export function InvoicingTab({ entityId, hasTaxNumber }: { entityId: string; hasTaxNumber: boolean }) {
  const settings = useQuery({ queryKey: ['/entities', entityId, 'invoicing'], queryFn: async () => (await api.get<InvoicingSettings>(`/entities/${entityId}/invoicing`)).data });
  if (!settings.data) return settings.isError ? <p className="text-[13px] text-ink-400">{(settings.error as Error).message}</p> : <SkeletonRows rows={6} cols={2} />;
  return <InvoicingEditor entityId={entityId} hasTaxNumber={hasTaxNumber} s={settings.data} />;
}

function InvoicingEditor({ entityId, hasTaxNumber, s }: { entityId: string; hasTaxNumber: boolean; s: InvoicingSettings }) {
  const qc = useQueryClient();
  const canEntities = useCan('entities.update');
  const canInvoices = useCan('invoices.update');
  const canEdit = canEntities || canInvoices;
  const canBanks = useCan('banking.read');
  const banks = useQuery({
    queryKey: ['/bank-accounts', entityId],
    queryFn: async () => (await api.get<Array<BankAccountSummary & { entityId: string }>>('/bank-accounts', { entityId })).data,
    enabled: canEdit && canBanks,
  });
  const [f, setF] = useState<Form>(() => toForm(s));
  const [saved, setSaved] = useState(false);
  const set = (p: Partial<Form>) => { setSaved(false); setF((x) => ({ ...x, ...p })); };

  const onSaved = (next: InvoicingSettings) => { qc.setQueryData(['/entities', entityId, 'invoicing'], next); setF(toForm(next)); };
  const save = useMutation({
    mutationFn: async () => {
      const body: InvoicingSettingsBody = {
        phone: nulled(f.phone), email: nulled(f.email), website: nulled(f.website), paymentTermsDays: Number(f.paymentTermsDays),
        paymentTermsLabel: nulled(f.paymentTermsLabel), bankAccountId: f.bankAccountId || null, footerNote: nulled(f.footerNote), documentTitle: f.documentTitle || null,
      };
      return (await api.put<InvoicingSettings>(`/entities/${entityId}/invoicing`, body)).data;
    },
    onSuccess: (next) => { onSaved(next); setSaved(true); },
  });

  // Active accounts of this company, plus the one already chosen even if it has since been switched off (so it shows).
  const bankOptions = (banks.data ?? []).filter((b) => b.entityId === entityId && (b.isActive || b.id === f.bankAccountId));
  const days = Number(f.paymentTermsDays);
  const daysValid = f.paymentTermsDays.trim() !== '' && Number.isInteger(days) && days >= 0 && days <= 120;
  const defaultTitle = hasTaxNumber ? 'VAT Invoice (the company has a VAT number)' : 'Invoice (no VAT number on the company)';

  return (
    <div className="space-y-2.5">
      <Card title="Logo" subtitle="Printed top right on every invoice">
        <LogoEditor entityId={entityId} settings={s} canEdit={canEdit} onChange={onSaved} />
      </Card>

      <Card title="On the invoice" subtitle="The company name, address, VAT and company numbers come from the company record">
        {canEdit ? (
          <div className="space-y-3">
            <FormGrid>
              <Field label="Phone"><PhoneInput autoComplete="off" value={f.phone} onChange={(phone) => set({ phone })} /></Field>
              <Field label="Email"><Input type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} placeholder="accounts@…" className="w-full" /></Field>
              <Wide><Field label="Website"><Input value={f.website} maxLength={120} onChange={(e) => set({ website: e.target.value })} placeholder="www.example.com" className="w-full" /></Field></Wide>
              <Field label="Payment terms (days)" hint="0–120. New invoices for this company default their due date to the issue date plus this.">
                <Input type="number" min={0} max={120} step={1} value={f.paymentTermsDays} onChange={(e) => set({ paymentTermsDays: e.target.value })} className="w-full" />
              </Field>
              <Field label="Terms wording" hint={`Leave blank to print "${daysValid ? (days === 0 ? 'Due on receipt' : `${days} days`) : '…'}"`}>
                <Input value={f.paymentTermsLabel} maxLength={60} onChange={(e) => set({ paymentTermsLabel: e.target.value })} placeholder="Due on receipt" className="w-full" />
              </Field>
              <Wide>
                <Field label="Bank account printed for payment" hint={canBanks ? 'Active accounts of this company. With none, the invoice says to contact the company to pay.' : 'You cannot list bank accounts; the current choice is kept.'}>
                  <Select value={f.bankAccountId} onChange={(e) => set({ bankAccountId: e.target.value })} disabled={!canBanks} className="w-full">
                    <option value="">None — "Contact the company to pay"</option>
                    {!canBanks && s.bankAccount && <option value={s.bankAccount.id}>{bankLine(s.bankAccount)}</option>}
                    {bankOptions.map((b) => <option key={b.id} value={b.id}>{bankLine(b)}{b.isActive ? '' : ' (inactive)'}</option>)}
                  </Select>
                </Field>
              </Wide>
              <Field label="Document title">
                <Select value={f.documentTitle} onChange={(e) => set({ documentTitle: e.target.value as Form['documentTitle'] })} className="w-full">
                  <option value="">Default — {defaultTitle}</option>
                  <option value="VAT_INVOICE">VAT Invoice</option>
                  <option value="INVOICE">Invoice</option>
                </Select>
              </Field>
              <Wide>
                <Field label="Footer note" hint={`${f.footerNote.length}/300 — printed under the totals, e.g. late-payment terms`}>
                  <Textarea rows={3} maxLength={300} value={f.footerNote} onChange={(e) => set({ footerNote: e.target.value })} className="w-full" />
                </Field>
              </Wide>
            </FormGrid>
            <FormError error={save.error} />
            <div className="flex items-center justify-end gap-3">
              {saved && !save.isPending && <span className="text-[12.5px] text-emerald-700">Saved</span>}
              <Button variant="secondary" onClick={() => { setF(toForm(s)); setSaved(false); save.reset(); }} disabled={save.isPending}>Reset</Button>
              <Button onClick={() => save.mutate()} disabled={!daysValid || save.isPending}>{save.isPending ? 'Saving…' : 'Save invoicing details'}</Button>
            </div>
          </div>
        ) : (
          <KeyValue items={[
            { label: 'Phone', value: formatPhone(s.phone) || '—' },
            { label: 'Email', value: s.email ?? '—' },
            { label: 'Website', value: s.website ?? '—' },
            { label: 'Terms', value: `${s.termsText}${s.paymentTermsLabel ? ` (${s.paymentTermsDays} days)` : ''}` },
            { label: 'Bank account', value: s.bankAccount ? bankLine(s.bankAccount) : 'None — "Contact the company to pay"' },
            { label: 'Document title', value: `${TITLE_LABEL[s.documentTitle]}${s.documentTitleIsDefault ? ' (default)' : ''}` },
            { label: 'Footer note', value: s.footerNote ?? '—' },
          ]} />
        )}
      </Card>
    </div>
  );
}

function LogoEditor({ entityId, settings, canEdit, onChange }: { entityId: string; settings: InvoicingSettings; canEdit: boolean; onChange: (s: InvoicingSettings) => void }) {
  const img = useStoredImage(settings.logoUrl);
  const input = useRef<HTMLInputElement>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: async (file: File) => (await api.post<InvoicingSettings>(`/entities/${entityId}/logo`, await fileToUpload(file))).data,
    onSuccess: onChange,
  });
  const remove = useMutation({ mutationFn: async () => (await api.delete<InvoicingSettings>(`/entities/${entityId}/logo`)).data, onSuccess: onChange });

  const pick = (file: File | undefined) => {
    setPickError(null);
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) { setPickError('Choose a PNG, JPEG or WebP image.'); return; }
    if (file.size > MAX_LOGO_BYTES) { setPickError('The logo must be 1 MB or smaller.'); return; }
    upload.mutate(file);
  };

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <div className="grid h-24 w-48 shrink-0 place-items-center rounded-lg border border-ink-200 bg-ink-50 p-2">
        {settings.logoUrl && img.src ? <img src={img.src} alt="Company logo" className="max-h-20 max-w-full object-contain" />
          : settings.logoUrl && !img.error ? <Skeleton className="h-16 w-full rounded-md" /> : <span className="flex flex-col items-center gap-1 text-[11.5px] text-ink-400"><ImageOff className="h-5 w-5" />{settings.logoUrl ? 'Could not load' : 'No logo'}</span>}
      </div>
      <div className="space-y-2">
        <p className="text-[12.5px] text-ink-500">PNG, JPEG or WebP, up to 1 MB. A wide logo on a transparent or white background prints best.</p>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <input ref={input} type="file" accept={LOGO_TYPES.join(',')} className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
            <Button size="sm" variant="secondary" onClick={() => input.current?.click()} disabled={upload.isPending}>
              <UploadIcon className="h-4 w-4" />{upload.isPending ? 'Uploading…' : settings.logoUrl ? 'Replace logo' : 'Upload logo'}
            </Button>
            {settings.logoUrl && <Button size="sm" variant="ghost" icon="Trash2" onClick={() => remove.mutate()} disabled={remove.isPending}>{remove.isPending ? 'Removing…' : 'Remove'}</Button>}
          </div>
        )}
        {pickError && <p className="text-[12px] text-red-600">{pickError}</p>}
        <FormError error={upload.error ?? remove.error} />
      </div>
    </div>
  );
}
